// Shared helpers for building writeTestPlan and readTestPlan nested objects.

import type {
  WriteTestPlan,
  ReadTestPlan,
  EmptyReadTestPlan,
} from '../../interfaces/api/models/testPlan.model';

export function buildEmptyReadTestPlan(
  system: 'ado' | 'qmetry',
): EmptyReadTestPlan {
  if (system === 'ado') {
    return {
      system: null,
      organization: null,
      project: null,
      planId: null,
      planName: null,
      suiteId: null,
      suiteName: null,
    };
  }

  return {
    system: null,
    projectId: null,
    projectName: null,
    subFolderId: null,
    subFolderName: null,
  };
}

/**
 * Builds the writeTestPlan nested object (always active — the write-back target).
 */
export function buildWriteTestPlan(
  system: 'ado' | 'qmetry',
  mappedProjectId: string,
  mappedProjectName: string,
  mappedProjectOrg: string,
  testPlanId: string,
  testPlanName: string,
  testSuiteId: string,
  testSuiteName: string,
): WriteTestPlan {
  if (system === 'ado') {
    return {
      system: 'ado',
      organization: mappedProjectOrg,
      project: mappedProjectName,
      planId: testPlanId,
      planName: testPlanName,
      suiteId: testSuiteId,
      suiteName: testSuiteName,
    };
  }
  return {
    system: 'qmetry',
    projectId: mappedProjectId,
    projectName: mappedProjectName,
    subFolderId: testSuiteId,
    subFolderName: testSuiteName,
  };
}

/**
 * Builds the readTestPlan nested object.
 * When no read seed is selected, all coordinate fields are null (object shape is preserved).
 * For ADO: active when existingTestSuiteId is set.
 * For QMetry: active when existingTestSuiteId (subFolderId) is set.
 */
export function buildReadTestPlan(
  system: 'ado' | 'qmetry',
  mappedProjectId: string,
  mappedProjectName: string,
  mappedProjectOrg: string,
  existingTestPlanId: string,
  existingTestPlanName: string,
  existingTestSuiteId: string,
  existingTestSuiteName: string,
): ReadTestPlan {
  const hasRead = Boolean(existingTestSuiteId);
  if (system === 'ado') {
    return {
      system: 'ado',
      organization: hasRead ? mappedProjectOrg : null,
      project: hasRead ? mappedProjectName : null,
      planId: hasRead ? existingTestPlanId : null,
      planName: hasRead ? existingTestPlanName : null,
      suiteId: hasRead ? existingTestSuiteId : null,
      suiteName: hasRead ? existingTestSuiteName : null,
    };
  }
  return {
    system: 'qmetry',
    projectId: hasRead ? mappedProjectId : null,
    projectName: hasRead ? mappedProjectName : null,
    subFolderId: hasRead ? existingTestSuiteId : null,
    subFolderName: hasRead ? existingTestSuiteName : null,
  };
}
